import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { PredefinedFilter } from '../../app/chargepoints-page/filter-options';
import { FilterCriteria, FilterCondition } from '../../app/chargepoints-page/chargepoints-page.component';

export interface CreateFilterDialogData {
  // Données d'entrée du dialog (pour l'instant vide)
}

export interface CreateFilterDialogResult {
  filter: PredefinedFilter | null;
}

@Component({
  selector: 'app-create-filter-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatIconModule,
    MatChipsModule,
    MatSelectModule,
    MatMenuModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './create-filter-dialog.component.html',
  styleUrls: ['./create-filter-dialog.component.scss']
})
export class CreateFilterDialogComponent {
  
  // Modèle unifié pour le filtre en construction
  public currentFilter: {
    name: string;
    criteria: FilterCriteria | null;
  } = {
    name: '',
    criteria: null
  };

  // Champs disponibles pour les filtres
  public availableFields = [
    { value: 'status', label: 'Status' },
    { value: 'lastHeartbeat', label: 'Last Heartbeat' },
  ];

  // Opérateurs disponibles
  public operators = [
    { value: 'equals', label: 'Equals (=)' },
    { value: 'notEquals', label: 'Not Equals (≠)' },
    { value: 'greaterThan', label: 'Greater Than (>)' },
    { value: 'lessThan', label: 'Less Than (<)' },
    { value: 'greaterThanOrEqual', label: 'Greater Than or Equal (≥)' },
    { value: 'lessThanOrEqual', label: 'Less Than or Equal (≤)' },
    { value: 'contains', label: 'Contains' },
    { value: 'notContains', label: 'Does Not Contain' },
    { value: 'startsWith', label: 'Starts With' },
    { value: 'endsWith', label: 'Ends With' },
    { value: 'in', label: 'In' },
    { value: 'notIn', label: 'Not In' },
    { value: 'between', label: 'Between' },
    { value: 'isNull', label: 'Is Null' },
    { value: 'isNotNull', label: 'Is Not Null' }
  ];

  constructor(
    public dialogRef: MatDialogRef<CreateFilterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateFilterDialogData
  ) {}

  // Méthodes pour gérer les critères
  addCondition(): void {
    if (!this.currentFilter.criteria) {
      this.currentFilter.criteria = {
        operator: 'AND',
        conditions: []
      };
    }
    
    const newCondition: FilterCondition = {
      field: '',
      operator: 'equals',
      value: ''
    };
    this.currentFilter.criteria.conditions.push(newCondition);
  }

  // Méthode pour obtenir les conditions typées
  getConditions(): FilterCondition[] {
    if (!this.currentFilter.criteria) return [];
    return this.currentFilter.criteria.conditions as FilterCondition[];
  }

  removeCondition(index: number): void {
    if (this.currentFilter.criteria) {
      this.currentFilter.criteria.conditions.splice(index, 1);
    }
  }

  // Méthodes pour les types d'input dynamiques
  getInputType(field: string, operator: string): string {
    if (this.isNullOperator(operator)) {
      return 'hidden';
    }
    
    switch (field) {
      case 'lastHeartbeat':
        return 'datetime-local';
        /*
      case 'type':
      case 'status':
        return 'select';
      */
      default:
        return 'text';
    }
  }

  isNullOperator(operator: string): boolean {
    return operator === 'isNull' || operator === 'isNotNull';
  }

  getFieldOptions(field: string): any[] {
    switch (field) {
      case 'type':
        return [
          { value: 'OCPP', label: 'OCPP' },
          { value: 'OKECHARGE', label: 'OKECHARGE' },
          { value: 'IVY', label: 'IVY' },
          { value: 'NETATMO', label: 'NETATMO' }
        ];
      case 'status':
        return [
          { value: 'AVAILABLE', label: 'Available' },
          { value: 'CONFIGURING', label: 'Configuring' },
          { value: 'PREPARING', label: 'Preparing' },
          { value: 'CHARGING', label: 'Charging' },
          { value: 'FINISHING', label: 'Finishing' },
          { value: 'UNREACHABLE', label: 'Unreachable' }
        ];
      default:
        return [];
    }
  }

  getValuePlaceholder(field: string, operator: string): string {
    if (this.isNullOperator(operator)) {
      return '';
    }
    
    switch (field) {
      case 'lastHeartbeat':
        return 'YYYY-MM-DDTHH:mm';
      case 'type':
      case 'status':
        return 'Select value';
      default:
        return 'Enter value';
    }
  }

  // Méthode pour coller depuis le clipboard
  async pasteFromClipboard(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      
      // Valider et parser le JSON
      try {
        const parsedCriteria = JSON.parse(text);
        this.currentFilter.criteria = parsedCriteria;
        console.log('Valid JSON pasted');
      } catch (err) {
        console.error('Invalid JSON:', err);
      }
    } catch (err) {
      console.error('Failed to read clipboard:', err);
    }
  }

  // Aperçu du filtre
  getFilterPreview(): FilterCriteria | null {
    return this.currentFilter.criteria;
  }

  isFilterValid(): boolean {
    const nameValid = this.currentFilter.name.length >= 3;
    const criteriaValid = this.currentFilter.criteria && 
      this.currentFilter.criteria.conditions.length > 0 && 
      this.currentFilter.criteria.conditions.every(c => (c as FilterCondition).field && (c as FilterCondition).operator);
    
    return nameValid && !!criteriaValid;
  }

  onCancel(): void {
    this.dialogRef.close({ filter: null });
  }

  onCreate(): void {
    if (this.isFilterValid()) {
      const randomId = 'custom_' + Math.random().toString(36).substr(2, 9);
      
      const newFilter: PredefinedFilter = {
        id: randomId,
        name: this.currentFilter.name,
        description: `Custom filter: ${this.currentFilter.name}`,
        criteria: this.currentFilter.criteria!
      };
      
      this.dialogRef.close({ filter: newFilter });
    }
  }

  // Méthodes pour le formatage textuel (comme dans chargepoints-page)
  getFilterPreviewText(): string {
    if (!this.currentFilter.criteria || this.currentFilter.criteria.conditions.length === 0) {
      return 'No filter criteria set yet';
    }
    
    return this.formatCriteriaAsText(this.currentFilter.criteria);
  }

  private formatCriteriaAsText(criteria: FilterCriteria): string {
    const conditions = criteria.conditions.map(condition => {
      if ('field' in condition) {
        // C'est une FilterCondition
        return this.formatConditionAsText(condition);
      } else {
        // C'est une FilterCriteria imbriquée
        return `(${this.formatCriteriaAsText(condition)})`;
      }
    });

    const operatorText = criteria.operator === 'AND' ? ' AND ' : 
                        criteria.operator === 'OR' ? ' OR ' : ' NOT ';
    
    return conditions.join(operatorText);
  }

  private formatConditionAsText(condition: FilterCondition): string {
    const fieldName = this.getFieldDisplayName(condition.field);
    const operatorText = this.getOperatorDisplayText(condition.operator);
    const valueText = this.formatValueForDisplay(condition.value);

    return `${fieldName} ${operatorText} ${valueText}`;
  }

  private getFieldDisplayName(field: string): string {
    const fieldMap: { [key: string]: string } = {
      'lastHeartbeat': 'Last Heartbeat',
      'type': 'Type',
      'status': 'Status',
      'connectorId': 'Connector ID',
      'location': 'Location'
    };
    return fieldMap[field] || field;
  }

  private getOperatorDisplayText(operator: string): string {
    const operatorMap: { [key: string]: string } = {
      'equals': '=',
      'notEquals': '≠',
      'greaterThan': '>',
      'lessThan': '<',
      'greaterThanOrEqual': '≥',
      'lessThanOrEqual': '≤',
      'contains': 'contains',
      'notContains': 'does not contain',
      'startsWith': 'starts with',
      'endsWith': 'ends with',
      'in': 'in',
      'notIn': 'not in',
      'between': 'between',
      'isNull': 'is null',
      'isNotNull': 'is not null'
    };
    return operatorMap[operator] || operator;
  }

  private formatValueForDisplay(value: any): string {
    if (value === null || value === undefined) {
      return 'null';
    }
    if (typeof value === 'string') {
      return `"${value}"`;
    }
    if (Array.isArray(value)) {
      return `[${value.map(v => this.formatValueForDisplay(v)).join(', ')}]`;
    }
    return String(value);
  }
}